import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import _ from 'lodash';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { CallHistoryBrowserBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-history-browser-behaviour/call-history-browser-behaviour.state';
import { CallTagsState } from 'src/app/store/samwin/call-tags/call-tags.state';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SamwinLineStatusRemoteDto } from 'src/generated/signalR/samwin-line-status-remote-dto';
import { CallHistoryEntryRemoteDto } from 'src/generated/swagger';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { SamwinCallListSelector } from '../samwin-call-list/samwin-call-list.selector';
import { SamwinCallListState } from '../samwin-call-list/samwin-call-list.state';
import { SamwinMessageLinesState } from '../samwin-message-lines/samwin-message-lines.state';
import { SamwinSkillsState } from '../samwin-skills/samwin-skills.state';
import { SkillGroup } from '../samwin-skills/skill-group';
import { ChatsState, ChatsStateModel } from './chats.state';
import { ChatDetails } from './models/chat-details';
import { ChatId } from './models/chat-id';
import { ChatMessage } from './models/chat-message';
import { ChatPeerDetails } from './models/chat-peer-details';
import { GuestStatus } from 'src/app/samwebDescription/samwin/GuestStatus';

@Injectable()
export class ChatsSelectors {
   private static readonly ChatPeerEmailTagName = 'CHAT_PEER_EMAIL';

   @Selector([EnabledFeaturesState.isFeatureEnabled(FeatureEnum.ALLOW_HANDLING_OF_ROCKET_CHAT_LIVECHATS)])
   public static canHandleChat(isFeatureEnabled: boolean): boolean {
      return isFeatureEnabled;
   }

   @Selector([SamwinMessageLinesState.getConnectedChatLines, SamwinCallListSelector.getMessageIdByCallId])
   public static getChatsWithConnectedLine(connectedChatLines: SamwinLineStatusRemoteDto[], getMessageId: (callId: number) => string): ChatId[] {
      return connectedChatLines.map((line) => new ChatId(line.callId, getMessageId(line.callId)));
   }

   @Selector([
      ChatsSelectors.canHandleChat,
      SamwinMessageLinesState.getAssignedChatLines,
      SamwinCallListSelector.getActiveOrRingingChatFromSharedSkill,
      SamwinCallListSelector.getMessageIdByCallId,
   ])
   public static getRingingChatId(
      isChatFeatureEnabled: boolean,
      assignedChatLines: SamwinLineStatusRemoteDto[],
      sharedChat: SamwinLineStatusRemoteDto,
      getMessageId: (callId: number) => string
   ): ChatId {
      if (!isChatFeatureEnabled) {
         return null;
      }

      if (_.isNil(assignedChatLines) || _.isEmpty(assignedChatLines)) {
         if (!_.isNil(sharedChat)) {
            return new ChatId(sharedChat.callId, getMessageId(sharedChat.callId));
         }
         return null;
      }
      const firstLine = _.head(assignedChatLines);
      if (!_.isNil(firstLine)) {
         return new ChatId(firstLine.callId, getMessageId(firstLine.callId));
      }
      return null;
   }

   @Selector([ChatsSelectors.getRingingChatId, ChatsSelectors.getChat])
   public static getRingingChat(chatId: ChatId, getChat: (chatId: ChatId) => ChatDetails): ChatDetails {
      if (_.isNull(chatId)) {
         return null; // no chats
      }
      return getChat(chatId);
   }

   @Selector([ChatsState])
   public static getOpenChats(state: ChatsStateModel): ChatId[] {
      return ChatsState.getOpenChats(state);
   }

   @Selector([ChatsSelectors.getChatsWithConnectedLine, ChatsSelectors.getOpenChats])
   public static getOpenChatsWithoutConnectedLine(chatsWithConnectedLine: ChatId[], openChats: ChatId[]): ChatId[] {
      return _.differenceWith(openChats, chatsWithConnectedLine, (lhs, rhs) => lhs.equals(rhs)).filter((chat) => _.isNil(chat.uid));
   }

   @Selector([ChatsSelectors.getChatsWithConnectedLine, ChatsSelectors.getOpenChats])
   public static getNotOpenChatsWithConnectedLine(chatsWithConnectedLine: ChatId[], openChats: ChatId[]): ChatId[] {
      return _.differenceWith(chatsWithConnectedLine, openChats, (lhs, rhs) => lhs.equals(rhs));
   }

   @Selector([ChatsSelectors.getChatsWithConnectedLine])
   public static isAnyActiveChats(chatsWithConnectedLine: ChatId[]): boolean {
      return chatsWithConnectedLine.length > 0;
   }

   @Selector([SamwinCallListState.getCallByIdFn])
   public static getChatPeerDetails(getCallByIdFn: (callId: number) => SamwinCallInfoRemoteDto): (chatId: ChatId) => ChatPeerDetails {
      return (chatId: ChatId) => {
         const waitListEntry = getCallByIdFn(chatId.callId);
         if (_.isNull(waitListEntry)) return new ChatPeerDetails(null, null);

         const tags = waitListEntry.tags ?? {};

         const name = _.isEmpty(waitListEntry.peerName) ? waitListEntry.peerId : waitListEntry.peerName;
         const email = tags[ChatsSelectors.ChatPeerEmailTagName];

         return new ChatPeerDetails(name, email);
      };
   }

   @Selector([ChatsSelectors.getChatFromCallList, ChatsSelectors.getChatFromCallHistory, SamwinCallListState.getCallByIdFn])
   public static getChat(
      getChatFromCallList: (chatId: ChatId) => ChatDetails,
      getChatFromCallHistory: (chatId: ChatId) => ChatDetails,
      getCallByIdFn: (callId: number) => null | SamwinCallInfoRemoteDto
   ): (chatId: ChatId) => ChatDetails {
      return (chatId: ChatId) => {
         const chatFromWaitList = getCallByIdFn(chatId.callId);
         if (_.isNil(chatFromWaitList)) {
            return getChatFromCallHistory(chatId);
         } else {
            return getChatFromCallList(chatId);
         }
      };
   }

   @Selector([ChatsState, SamwinSkillsState.getSkillGroupById, ChatsSelectors.getChatPeerDetails, SamwinCallListState.getCallByIdFn])
   public static getChatFromCallList(
      state: ChatsStateModel,
      getSkillGroupById: (skillId: number) => SkillGroup,
      getChatPeerDetails: (chatId: ChatId) => ChatPeerDetails,
      getCallByIdFn: (callId: number) => null | SamwinCallInfoRemoteDto
   ): (chatId: ChatId) => ChatDetails {
      return (chatId: ChatId) => {
         const [idx, chatState] = ChatsState.getChatState(state, chatId);
         const chatFromWaitList = getCallByIdFn(chatId.callId);
         if (_.isNull(chatFromWaitList)) {
            return null;
         }
         const skillId = chatFromWaitList?.queueId;

         const chat = chatState.chatDetails ?? new ChatDetails(chatId, '', false, skillId);
         return chat.enrich(skillId, getSkillGroupById(skillId), getChatPeerDetails(chatId));
      };
   }

   @Selector([ChatsState, SamwinSkillsState.getSkillGroupById, CallTagsState.getHistoryCallTagsForCall, CallHistoryBrowserBehaviourState.getHistoryEntryFn])
   public static getChatFromCallHistory(
      state: ChatsStateModel,
      getSkillGroupById: (skillId: number) => SkillGroup,
      getTagsFromHistory: (uniqueCallId: string) => { [key: string]: string },
      getHistoryEntryFn: (uniqueCallId: string) => CallHistoryEntryRemoteDto
   ): (chatId: ChatId) => ChatDetails {
      return (chatId: ChatId) => {
         const [idx, chatState] = ChatsState.getChatState(state, chatId);

         const uid = _.isNil(chatState.chatDetails) ? chatId.uid : chatState.chatDetails.id.uid;

         const tags = getTagsFromHistory(uid);
         const historyEntry = getHistoryEntryFn(uid);

         if (_.isNil(historyEntry)) return chatState.chatDetails;

         const name = _.isEmpty(historyEntry.peerName) ? historyEntry.peerId : historyEntry.peerName;
         const email = tags[ChatsSelectors.ChatPeerEmailTagName];

         const chat = chatState.chatDetails ?? new ChatDetails(chatId, '', true, historyEntry.opextQueueId);
         return chat.enrich(historyEntry.opextQueueId, getSkillGroupById(historyEntry.opextQueueId), new ChatPeerDetails(name, email));
      };
   }

   @Selector([ChatsState])
   public static getNumberOfUnreadMessages(state: ChatsStateModel): (chatId: ChatId) => number | null {
      return (chatId: ChatId) => {
         const [idx, chatState] = ChatsState.getChatState(state, chatId);
         return chatState.numberOfUnreadMessages;
      };
   }

   @Selector([ChatsState])
   public static getNumberOfUnreadMessagesForAllChats(state: ChatsStateModel): Map<ChatId, number | null> {
      return new Map(state.chats.map((chat) => [chat.chatId, chat.numberOfUnreadMessages]));
   }

   @Selector([ChatsState])
   public static getTotalNumberOfUnreadMessages(state: ChatsStateModel): number {
      return state.chats.reduce((total, chat) => total + chat.numberOfUnreadMessages ?? 0, 0);
   }

   @Selector([ChatsState])
   public static getLastMessage(state: ChatsStateModel): (chatId: ChatId) => ChatMessage | null {
      return (chatId: ChatId) => {
         const [idx, chatState] = ChatsState.getChatState(state, chatId);
         return chatState.lastMessage;
      };
   }

   @Selector([ChatsState])
   public static getUserToken(state: ChatsStateModel): string {
      return state.userToken;
   }

   @Selector([ChatsState])
   public static wasClosedByPeer(state: ChatsStateModel): (chatId: ChatId) => boolean {
      return (chatId: ChatId) => {
         const [idx, chatState] = ChatsState.getChatState(state, chatId);
         return chatState.wasClosedByPeer;
      };
   }

   @Selector([ChatsState])
   public static getGuestStatus(state: ChatsStateModel): (chatId: ChatId) => GuestStatus {
      return (chatId: ChatId) => {
         const [idx, chatState] = ChatsState.getChatState(state, chatId);
         return chatState.guestStatus;
      };
   }
}
